import { z } from 'zod';
import {
  DiscountTypeEnum,
  ExperienceTypeEnum,
  FlowVariantEnum,
  FrequencyCapEnum,
  FunnelStatusEnum,
  FunnelTypeEnum,
  LayoutModeEnum,
  MatchModeEnum,
  OfferModeEnum,
  OfferSetSelectionModeEnum,
  PlacementEnum,
  StepTypeEnum,
  TemplateCategoryEnum,
  TemplateStatusEnum,
  TrafficTypeEnum,
  TriggerLifecycleEnum,
} from '../taxonomy';
import {
  IdSchema,
  JsonValueSchema,
  MessageResponseSchema,
  OptionalNullableStringSchema,
  PaginationSchema,
  TimestampSchema,
} from './common';
import { StepHookSchema, StepListenerSchema } from './structure';

const NullableNumberSchema = z.number().nullable().optional();
const NullableIntegerSchema = z.number().int().nullable().optional();
const NullableBooleanSchema = z.boolean().nullable().optional();
const LooseExperienceTypeSchema = ExperienceTypeEnum.or(z.string());
const LooseFlowVariantSchema = FlowVariantEnum.or(z.string());
const LooseFrequencyCapSchema = FrequencyCapEnum.or(z.string());
const LooseFunnelTypeSchema = FunnelTypeEnum.or(z.string());
const LoosePlacementSchema = PlacementEnum.or(z.string());
const LooseStepTypeSchema = StepTypeEnum.or(z.string());
const LooseTrafficTypeSchema = TrafficTypeEnum.or(z.string());
const LooseTriggerLifecycleSchema = TriggerLifecycleEnum.or(z.string());

export const FunnelRuleSchema = z.object({
  id: IdSchema,
  tenantId: IdSchema,
  funnelId: IdSchema,
  ruleType: z.string(),
  comparator: z.string(),
  value: OptionalNullableStringSchema,
  productIds: z.array(z.string()).nullable().optional(),
  isEnabled: z.boolean(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  deletedAt: TimestampSchema.nullable().optional(),
});

export const OfferSchema = z.object({
  id: IdSchema,
  tenantId: IdSchema,
  storeId: OptionalNullableStringSchema,
  name: z.string().min(1),
  description: OptionalNullableStringSchema,
  discountType: DiscountTypeEnum.nullable().optional(),
  discountValue: NullableNumberSchema,
  isActive: z.boolean(),
  validFrom: TimestampSchema.nullable().optional(),
  validUntil: TimestampSchema.nullable().optional(),
  minPurchaseAmount: NullableNumberSchema,
  maxUses: NullableIntegerSchema,
  currentUses: NullableIntegerSchema,
  productIds: z.array(z.string()).nullable().optional(),
  funnelId: OptionalNullableStringSchema,
  stepId: OptionalNullableStringSchema,
  productId: OptionalNullableStringSchema,
  variantId: OptionalNullableStringSchema,
  headline: OptionalNullableStringSchema,
  imageUrl: OptionalNullableStringSchema,
  ctaText: OptionalNullableStringSchema,
  declineText: OptionalNullableStringSchema,
  pricingType: OptionalNullableStringSchema,
  pricingValue: NullableNumberSchema,
  originalPrice: NullableNumberSchema,
  shippingType: OptionalNullableStringSchema,
  shippingValue: NullableNumberSchema,
  displayQuantity: NullableBooleanSchema,
  limitQuantity: NullableBooleanSchema,
  maxQuantity: NullableIntegerSchema,
  bannerText: OptionalNullableStringSchema,
  componentsConfig: JsonValueSchema.nullable().optional(),
  subscriptionType: OptionalNullableStringSchema,
  subscriptionName: OptionalNullableStringSchema,
  parentOfferId: OptionalNullableStringSchema,
  minStock: NullableIntegerSchema,
  fallbackProductId: OptionalNullableStringSchema,
  offerType: OptionalNullableStringSchema,
  guaranteeText: OptionalNullableStringSchema,
  microcopy: OptionalNullableStringSchema,
  urlSlug: OptionalNullableStringSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  deletedAt: TimestampSchema.nullable().optional(),
});

export const FunnelStepSchema = z.object({
  id: IdSchema,
  tenantId: IdSchema,
  storeId: OptionalNullableStringSchema,
  funnelId: IdSchema,
  name: z.string().min(1),
  isActive: z.boolean(),
  stepType: StepTypeEnum,
  position: z.number().int().nonnegative(),
  offerId: OptionalNullableStringSchema,
  offerSetId: OptionalNullableStringSchema,
  collectionId: OptionalNullableStringSchema,
  pageContent: JsonValueSchema.nullable().optional(),
  nextStepAccept: OptionalNullableStringSchema,
  nextStepDecline: OptionalNullableStringSchema,
  transitions: JsonValueSchema.nullable().optional(),
  experienceType: LooseExperienceTypeSchema.nullable().optional(),
  rules: JsonValueSchema.nullable().optional(),
  placement: LoosePlacementSchema.nullable().optional(),
  placementUrlPattern: OptionalNullableStringSchema,
  frequencyCap: LooseFrequencyCapSchema.nullable().optional(),
  timeoutSeconds: NullableIntegerSchema,
  actions: JsonValueSchema.nullable().optional(),
  listeners: z.array(StepListenerSchema),
  hook: StepHookSchema.nullable().optional(),
  templateId: OptionalNullableStringSchema,
  layoutMode: LayoutModeEnum.default('card'),
  isEntry: z.boolean().default(false),
  isTemplateModified: z.boolean().default(false),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  deletedAt: TimestampSchema.nullable().optional(),
  offer: OfferSchema.nullable().optional(),
});

export const RuntimeFunnelStepSchema = z.object({
  id: IdSchema,
  name: z.string().min(1),
  stepType: StepTypeEnum,
  position: z.number().int().nonnegative(),
  offerId: OptionalNullableStringSchema,
  offerSetId: OptionalNullableStringSchema,
  collectionId: OptionalNullableStringSchema,
  productIds: z.array(z.string()).nullable().optional(),
  pageContent: JsonValueSchema.nullable().optional(),
  nextStepAccept: OptionalNullableStringSchema,
  nextStepDecline: OptionalNullableStringSchema,
  transitions: JsonValueSchema.nullable().optional(),
  experienceType: LooseExperienceTypeSchema.nullable().optional(),
  placement: LoosePlacementSchema.nullable().optional(),
  placementUrlPattern: OptionalNullableStringSchema,
  frequencyCap: LooseFrequencyCapSchema.nullable().optional(),
  timeoutSeconds: NullableIntegerSchema,
  actions: JsonValueSchema.nullable().optional(),
  listeners: z.array(StepListenerSchema),
  hook: StepHookSchema.nullable().optional(),
  templateId: OptionalNullableStringSchema,
  layoutMode: LayoutModeEnum.default('card'),
  isEntry: z.boolean().default(false),
  isTemplateModified: z.boolean().default(false),
  offer: OfferSchema.nullable().optional(),
  offers: z.array(OfferSchema).nullable().optional(),
  inherited: JsonValueSchema.optional(),
  triggerLifecycle: LooseTriggerLifecycleSchema.optional(),
});

export const FunnelSchema = z.object({
  id: IdSchema,
  tenantId: IdSchema,
  storeId: OptionalNullableStringSchema,
  name: z.string().min(1),
  description: OptionalNullableStringSchema,
  isActive: z.boolean(),
  isDefault: NullableBooleanSchema,
  priority: NullableIntegerSchema,
  funnelType: LooseFunnelTypeSchema.nullable().optional(),
  trafficType: LooseTrafficTypeSchema.nullable().optional(),
  countries: z.array(z.string()).nullable().optional(),
  landingPageUrls: z.array(z.string()).nullable().optional(),
  rules: JsonValueSchema.nullable().optional(),
  version: z.number().int().nonnegative(),
  productId: OptionalNullableStringSchema,
  matchMode: MatchModeEnum.default('any'),
  status: FunnelStatusEnum.or(z.string()).default('draft'),
  startDate: TimestampSchema.nullable().optional(),
  endDate: TimestampSchema.nullable().optional(),
  flowVariant: LooseFlowVariantSchema.nullable().optional(),
  triggerLifecycle: LooseTriggerLifecycleSchema.nullable().optional(),
  offerMode: OfferModeEnum.default('step'),
  funnelOfferId: OptionalNullableStringSchema,
  activationTrigger: StepHookSchema.nullable().optional(),
  attributionTrigger: StepHookSchema.nullable().optional(),
  firstUrl: OptionalNullableStringSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  deletedAt: TimestampSchema.nullable().optional(),
  steps: z.array(FunnelStepSchema).optional(),
});

export const OfferSetSchema = z.object({
  id: IdSchema,
  tenantId: IdSchema,
  name: z.string().min(1),
  description: OptionalNullableStringSchema,
  selectionMode: OfferSetSelectionModeEnum,
  isActive: z.boolean(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  deletedAt: TimestampSchema.nullable().optional(),
});

export const OfferSetItemSchema = z.object({
  id: IdSchema,
  tenantId: IdSchema,
  offerSetId: IdSchema,
  offerId: IdSchema,
  weight: z.number().nonnegative(),
  position: z.number().int().nonnegative(),
  createdAt: TimestampSchema,
});

export const TemplateSchema = z.object({
  id: IdSchema,
  tenantId: IdSchema,
  name: z.string().min(1),
  description: OptionalNullableStringSchema,
  category: TemplateCategoryEnum,
  status: TemplateStatusEnum,
  thumbnailUrl: OptionalNullableStringSchema,
  author: OptionalNullableStringSchema,
  componentTree: JsonValueSchema.nullable().optional(),
  tags: z.array(z.string()).default([]),
  activeFunnelsCount: z.number().int().nonnegative().default(0),
  componentCount: z.number().int().nonnegative().default(0),
  displaySettings: JsonValueSchema.nullable().optional(),
  version: z.number().int().nonnegative(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  deletedAt: TimestampSchema.nullable().optional(),
});

export const LineItemSchema = z.object({
  productId: z.string(),
  variantId: z.string().optional(),
  quantity: z.number().int().positive(),
  price: z.number().nonnegative(),
  tags: z.array(z.string()).optional(),
});

export const PageContextSchema = z.object({
  pageType: z.string(),
  url: z.string(),
  storeId: z.string().optional(),
  productId: z.string(),
  collectionId: z.string().optional(),
  productCollections: z.array(z.string()),
  productTags: z.array(z.string()),
  cartTotal: z.number().int().nonnegative(),
  cartItemCount: z.number().int().nonnegative(),
  cartProductIds: z.array(z.string()),
  cartCollections: z.array(z.string()),
  isReturning: z.boolean(),
  pageViewCount: z.number().int().nonnegative(),
  orderProductIds: z.array(z.string()),
  orderTotal: z.number().int().nonnegative(),
  deviceType: z.string().optional(),
  country: z.string().optional(),
  browserLanguage: z.string().optional(),
  landingUrl: z.string().optional(),
  urlParameters: z.record(z.string()).optional(),
  dayOfWeek: z.number().int().min(0).max(6).nullable().optional(),
  timeOfDay: z.string().optional(),
  currentDate: z.string().optional(),
  sessionCount: z.number().int().nonnegative().optional(),
  purchaseHistoryProductIds: z.array(z.string()).optional(),
  previousFunnelIds: z.array(z.string()).optional(),
  customerTags: z.array(z.string()).optional(),
  cartProductTags: z.array(z.string()).optional(),
  timeOnPageSec: z.number().int().nonnegative().optional(),
  scrollDepthPct: z.number().int().min(0).max(100).optional(),
  exitIntent: z.boolean().optional(),
});

export const CreateFunnelRequestSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  isActive: z.boolean().default(false),
  priority: z.number().int().optional(),
  funnelType: LooseFunnelTypeSchema.optional(),
  trafficType: LooseTrafficTypeSchema.optional(),
  flowVariant: LooseFlowVariantSchema.optional(),
  triggerLifecycle: LooseTriggerLifecycleSchema.optional(),
  status: FunnelStatusEnum.optional(),
  countries: z.array(z.string()).optional(),
  landingPageUrls: z.array(z.string()).optional(),
  productId: z.string().optional(),
  activationTrigger: StepHookSchema.optional(),
  attributionTrigger: StepHookSchema.optional(),
  firstUrl: z.string().optional(),
  offerMode: OfferModeEnum.optional(),
  funnelOfferId: z.string().optional(),
  storeId: z.string().optional(),
});

export const UpdateFunnelRequestSchema =
  CreateFunnelRequestSchema.partial().extend({
    isDefault: z.boolean().optional(),
    startDate: TimestampSchema.optional(),
    endDate: TimestampSchema.optional(),
    rules: JsonValueSchema.optional(),
  });

export const CreateFunnelStepRequestSchema = z.object({
  name: z.string().min(1),
  stepType: StepTypeEnum,
  position: z.number().int().nonnegative().default(0),
  isActive: z.boolean().optional(),
  offerId: z.string().optional(),
  offerSetId: z.string().optional(),
  collectionId: z.string().optional(),
  nextStepAccept: z.string().optional(),
  nextStepDecline: z.string().optional(),
  transitions: JsonValueSchema.optional(),
  experienceType: LooseExperienceTypeSchema.optional(),
  placement: LoosePlacementSchema.optional(),
  placementUrlPattern: z.string().optional(),
  frequencyCap: LooseFrequencyCapSchema.optional(),
  triggerRules: JsonValueSchema.optional(),
  timeoutSeconds: z.number().int().nonnegative().optional(),
  actions: JsonValueSchema.optional(),
  listeners: z.array(StepListenerSchema),
  hook: StepHookSchema.optional(),
  templateId: z.string().optional(),
  pageContent: JsonValueSchema.optional(),
  layoutMode: LayoutModeEnum.optional(),
  isEntry: z.boolean().optional(),
});

export const UpdateFunnelStepRequestSchema =
  CreateFunnelStepRequestSchema.partial();

export const CreateOfferRequestSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  discountType: DiscountTypeEnum.optional(),
  discountValue: z.number().positive().optional(),
  isActive: z.boolean().default(true),
  productId: z.string().optional(),
  variantId: z.string().optional(),
  headline: z.string().optional(),
  imageUrl: z.string().optional(),
  ctaText: z.string().optional(),
  declineText: z.string().optional(),
  pricingType: z.string().optional(),
  pricingValue: z.number().optional(),
  originalPrice: z.number().optional(),
  shippingType: z.string().optional(),
  shippingValue: z.number().optional(),
  displayQuantity: z.boolean().optional(),
  limitQuantity: z.boolean().optional(),
  maxQuantity: z.number().int().positive().optional(),
  bannerText: z.string().optional(),
  subscriptionType: z.string().optional(),
  subscriptionName: z.string().optional(),
  funnelId: z.string().optional(),
  stepId: z.string().optional(),
  parentOfferId: z.string().optional(),
  minStock: z.number().int().nonnegative().optional(),
  fallbackProductId: z.string().optional(),
  offerType: z.string().optional(),
  guaranteeText: z.string().optional(),
  microcopy: z.string().optional(),
  urlSlug: z.string().optional(),
});

export const UpdateOfferRequestSchema = CreateOfferRequestSchema.partial().extend(
  {
    description: z.string().nullable().optional(),
    displayQuantity: z.boolean().nullable().optional(),
    limitQuantity: z.boolean().nullable().optional(),
    maxQuantity: z.number().int().positive().nullable().optional(),
    funnelId: z.string().nullable().optional(),
    stepId: z.string().nullable().optional(),
    parentOfferId: z.string().nullable().optional(),
    minStock: z.number().int().nonnegative().nullable().optional(),
    fallbackProductId: z.string().nullable().optional(),
    urlSlug: z.string().nullable().optional(),
  },
);

export const CreateRuleRequestSchema = z.object({
  ruleType: z.string().min(1),
  comparator: z.string().min(1),
  value: z.string().optional(),
  productIds: z.array(z.string()).optional(),
});

export const UpdateRuleRequestSchema = CreateRuleRequestSchema.partial().extend({
  isEnabled: z.boolean().optional(),
});

export const CreateOfferSetRequestSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  selectionMode: OfferSetSelectionModeEnum.default('weighted'),
  isActive: z.boolean().default(true),
});

export const UpdateOfferSetRequestSchema =
  CreateOfferSetRequestSchema.partial();

export const CreateOfferSetItemRequestSchema = z.object({
  offerId: z.string().min(1),
  weight: z.number().nonnegative().default(1),
  position: z.number().int().nonnegative().default(0),
});

export const CreateTemplateRequestSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  category: TemplateCategoryEnum,
  status: TemplateStatusEnum.optional(),
  thumbnailUrl: z.string().nullable().optional(),
  author: z.string().nullable().optional(),
  componentTree: JsonValueSchema.optional(),
  tags: z.array(z.string()).optional(),
  displaySettings: JsonValueSchema.optional(),
});

export const UpdateTemplateRequestSchema =
  CreateTemplateRequestSchema.partial();

export const EvaluateRequestSchema = z.object({
  tenantId: z.string().optional(),
  cartId: z.string(),
  subtotal: z.number().nonnegative(),
  currency: z.string(),
  itemCount: z.number().int().nonnegative(),
  lineItems: z.array(LineItemSchema),
  country: z.string(),
  customerTags: z.array(z.string()).optional(),
});

export const EvaluateStepRequestSchema = z.object({
  tenantId: z.string().optional(),
  sessionId: z.string(),
  funnelId: z.string(),
  customerId: z.string().optional(),
  customerCurrency: z.string().optional(),
  currentStepId: z.string().optional(),
  event: z.string().optional(),
  pageContext: PageContextSchema,
});

export const AcceptStepRequestSchema = z.object({
  sessionId: z.string().optional(),
  action: z.string().optional(),
  /** Identifies the exact server-owned offer selected from a multi-offer step. */
  offerId: z.string().optional(),
  productId: z.string().optional(),
  variantId: z.string().optional(),
  quantity: z.number().int().optional(),
  orderId: z.string().optional(),
  /** Binds a server-signed pre-checkout offer token to one cart. */
  cartToken: z.string().uuid().optional(),
});

export const DeclineStepRequestSchema = z.object({
  sessionId: z.string().optional(),
});

/**
 * D-C: server-validated `go_to_step` navigation. The storefront asks the server
 * to jump to `targetStepId`; funnels-go only honors it when the current step's
 * listeners authored a `go_to_step` navigate reaction to that target — so a
 * client cannot navigate to an arbitrary step.
 */
export const NavigateStepRequestSchema = z.object({
  sessionId: z.string().optional(),
  targetStepId: IdSchema,
});

export const AssignFunnelRequestSchema = z.object({
  tenantId: z.string().optional(),
  sessionId: z.string(),
  customerId: z.string().optional(),
  customerCurrency: z.string().optional(),
  customerCountry: z.string().optional(),
  referrerSource: z.string().optional(),
  deviceType: z.string().optional(),
  urlPath: z.string().optional(),
  pageType: z.string().optional(),
  pageContext: PageContextSchema.optional(),
});

export const FunnelResponseSchema = z.object({ funnel: FunnelSchema });
export const FunnelListResponseSchema = z.object({
  funnels: z.array(FunnelSchema),
  pagination: PaginationSchema,
});
export const FunnelStepResponseSchema = z.object({ step: FunnelStepSchema });
export const FunnelStepListResponseSchema = z.object({
  steps: z.array(FunnelStepSchema),
});
export const RuntimeFunnelSummarySchema = z.object({
  id: IdSchema,
  name: z.string(),
  totalSteps: z.number().int().nonnegative(),
});

export const FunnelStepDetailResponseSchema = z.object({
  data: z.object({
    funnel: RuntimeFunnelSummarySchema,
    step: RuntimeFunnelStepSchema,
  }),
});
export const StepOfferResponseSchema = z.object({
  funnel: RuntimeFunnelSummarySchema,
  step: RuntimeFunnelStepSchema,
});
export const StepPageBlockResponseSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  props: z.record(JsonValueSchema),
});
export const StepPageResponseSchema = z.object({
  blocks: z.array(StepPageBlockResponseSchema),
});
export const OfferResponseSchema = z.object({ offer: OfferSchema });
export const OfferListResponseSchema = z.object({ offers: z.array(OfferSchema) });
export const OfferSetResponseSchema = z.object({
  offerSet: OfferSetSchema,
  items: z.array(OfferSetItemSchema).optional(),
});
export const OfferSetListResponseSchema = z.object({
  offerSets: z.array(OfferSetSchema),
});
export const OfferSetItemResponseSchema = z.object({
  item: OfferSetItemSchema,
});
export const TemplateResponseSchema = z.object({ template: TemplateSchema });
export const TemplateListResponseSchema = z.object({
  templates: z.array(TemplateSchema),
  pagination: PaginationSchema,
});
export const RuleResponseSchema = z.object({ rule: FunnelRuleSchema });
export const RuleListResponseSchema = z.object({
  rules: z.array(FunnelRuleSchema),
});
export const ConfigOptionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
  description: z.string().optional(),
});
export const ConfigOptionsResponseSchema = z.object({
  action_types: z.array(ConfigOptionSchema),
  countries: z.array(ConfigOptionSchema),
  experience_types: z.array(ConfigOptionSchema),
  flow_variants: z.array(ConfigOptionSchema),
  frequency_caps: z.array(ConfigOptionSchema),
  funnel_types: z.array(ConfigOptionSchema),
  step_types: z.array(ConfigOptionSchema),
  traffic_types: z.array(ConfigOptionSchema),
  trigger_lifecycles: z.array(ConfigOptionSchema),
});
export const FunnelSearchResponseSchema = z.object({
  funnels: z.array(FunnelSchema),
});
export const FunnelAnalyticsMetricsSchema = z.object({
  impressions: z.number().int().nonnegative(),
  conversions: z.number().int().nonnegative(),
  conversionRate: z.number().nonnegative(),
  revenue: z.number().nonnegative(),
});
export const FunnelAnalyticsTopFunnelSchema = z.object({
  id: IdSchema,
  name: z.string(),
  impressions: z.number().int().nonnegative(),
  conversions: z.number().int().nonnegative(),
});
export const FunnelAnalyticsResponseSchema = z.object({
  totalFunnels: z.number().int().nonnegative(),
  activeFunnels: z.number().int().nonnegative(),
  totalSteps: z.number().int().nonnegative(),
  totalOffers: z.number().int().nonnegative(),
  metrics: FunnelAnalyticsMetricsSchema,
  topFunnels: z.array(FunnelAnalyticsTopFunnelSchema),
});
export const FunnelStepAnalyticsSchema = z.object({
  stepId: IdSchema,
  stepName: z.string(),
  impressions: z.number().int().nonnegative(),
  accepts: z.number().int().nonnegative(),
  declines: z.number().int().nonnegative(),
  conversionRate: z.number().nonnegative(),
  revenue: z.number().nonnegative(),
});
export const FunnelDetailAnalyticsResponseSchema = z.object({
  funnelId: IdSchema,
  period: z.string().min(1),
  summary: z.object({
    totalSessions: z.number().int().nonnegative(),
    conversionRate: z.number().nonnegative(),
    totalRevenue: z.number().nonnegative(),
    averageOrderValue: z.number().nonnegative(),
  }),
  steps: z.array(FunnelStepAnalyticsSchema),
});
export const ChangelogEntrySchema = z.object({
  id: IdSchema,
  tenantId: IdSchema,
  entityType: z.string().min(1),
  entityId: IdSchema,
  action: z.string().min(1),
  changes: JsonValueSchema.nullable().optional(),
  actor: OptionalNullableStringSchema,
  actorEmail: OptionalNullableStringSchema,
  ipAddress: OptionalNullableStringSchema,
  userAgent: OptionalNullableStringSchema,
  createdAt: TimestampSchema,
});
export const FunnelChangelogResponseSchema = z.object({
  changelog: z.array(ChangelogEntrySchema),
});
export const FunnelVersionSchema = z.object({
  id: IdSchema,
  version: z.number().int().positive(),
  action: z.string().min(1),
  actor: z.string().min(1),
  changes: JsonValueSchema.nullable().optional(),
  createdAt: TimestampSchema,
});
export const FunnelVersionsResponseSchema = z.object({
  versions: z.array(FunnelVersionSchema),
});
export const PricingViewStepSchema = z.object({
  stepId: IdSchema,
  stepName: z.string(),
  stepType: LooseStepTypeSchema,
  position: z.number().int().nonnegative(),
  offerId: OptionalNullableStringSchema,
  offerSetId: OptionalNullableStringSchema,
  offerName: OptionalNullableStringSchema,
  discountType: OptionalNullableStringSchema,
  discountValue: z.number().nonnegative().nullable().optional(),
  offerActive: z.boolean(),
  offers: z.array(OfferSchema).optional(),
});
export const PricingViewEntrySchema = z.object({
  funnelId: IdSchema,
  funnelName: z.string(),
  isActive: z.boolean(),
  funnelType: LooseFunnelTypeSchema.nullable().optional(),
  priority: z.number().int(),
  stepsCount: z.number().int().nonnegative(),
  steps: z.array(PricingViewStepSchema),
});
export const PricingViewResponseSchema = z.object({
  entries: z.array(PricingViewEntrySchema),
});
export const OfferUsageStepSchema = z.object({
  stepId: z.string(),
  usageType: z.string().min(1),
});
export const OfferUsageFunnelSchema = z.object({
  funnelName: z.string(),
  steps: z.array(OfferUsageStepSchema),
});
export const OfferUsageResponseSchema = z.object({
  usage: z.object({
    offerId: IdSchema,
    usageCount: z.number().int().nonnegative(),
    funnels: z.array(OfferUsageFunnelSchema),
  }),
});
export const EvaluateFunnelResponseSchema = z.object({
  matched: z.boolean(),
  funnelId: z.string().optional(),
  funnelName: z.string().optional(),
  isDefault: z.boolean().optional(),
  firstStep: RuntimeFunnelStepSchema.nullable().optional(),
});
export const EvaluateStepResponseSchema = z.union([
  z.object({
    hasExperience: z.literal(true),
    step: RuntimeFunnelStepSchema,
    reason: z.string().optional(),
  }),
  z.object({
    hasExperience: z.literal(false),
    reason: z.string().optional(),
  }),
]);
export const StepActionResponseSchema = z.union([
  z.object({
    complete: z.literal(true),
    /** Signed, short-lived proof of server-authoritative offer terms. */
    acceptedOfferToken: z.string().min(1).optional(),
  }),
  z.object({
    complete: z.literal(false),
    nextStep: RuntimeFunnelStepSchema,
    /** Signed, short-lived proof of server-authoritative offer terms. */
    acceptedOfferToken: z.string().min(1).optional(),
  }),
]);
export const DeleteResponseSchema = MessageResponseSchema;

export type Funnel = z.infer<typeof FunnelSchema>;
export type FunnelStep = z.infer<typeof FunnelStepSchema>;
export type RuntimeFunnelStep = z.infer<typeof RuntimeFunnelStepSchema>;
export type Offer = z.infer<typeof OfferSchema>;
export type OfferSet = z.infer<typeof OfferSetSchema>;
export type OfferSetItem = z.infer<typeof OfferSetItemSchema>;
export type FunnelRule = z.infer<typeof FunnelRuleSchema>;
export type Template = z.infer<typeof TemplateSchema>;
export type LineItem = z.infer<typeof LineItemSchema>;
export type PageContext = z.infer<typeof PageContextSchema>;
export type RuntimeFunnelSummary = z.infer<typeof RuntimeFunnelSummarySchema>;
export type StepPageBlockResponse = z.infer<
  typeof StepPageBlockResponseSchema
>;
export type StepPageResponse = z.infer<typeof StepPageResponseSchema>;
export type StepOfferResponse = z.infer<typeof StepOfferResponseSchema>;
export type ConfigOption = z.infer<typeof ConfigOptionSchema>;
export type ConfigOptionsResponse = z.infer<
  typeof ConfigOptionsResponseSchema
>;
export type FunnelSearchResponse = z.infer<typeof FunnelSearchResponseSchema>;
export type FunnelAnalyticsMetrics = z.infer<
  typeof FunnelAnalyticsMetricsSchema
>;
export type FunnelAnalyticsTopFunnel = z.infer<
  typeof FunnelAnalyticsTopFunnelSchema
>;
export type FunnelAnalyticsResponse = z.infer<
  typeof FunnelAnalyticsResponseSchema
>;
export type FunnelStepAnalytics = z.infer<typeof FunnelStepAnalyticsSchema>;
export type FunnelDetailAnalyticsResponse = z.infer<
  typeof FunnelDetailAnalyticsResponseSchema
>;
export type ChangelogEntry = z.infer<typeof ChangelogEntrySchema>;
export type FunnelChangelogResponse = z.infer<
  typeof FunnelChangelogResponseSchema
>;
export type FunnelVersion = z.infer<typeof FunnelVersionSchema>;
export type FunnelVersionsResponse = z.infer<
  typeof FunnelVersionsResponseSchema
>;
export type PricingViewStep = z.infer<typeof PricingViewStepSchema>;
export type PricingViewEntry = z.infer<typeof PricingViewEntrySchema>;
export type PricingViewResponse = z.infer<typeof PricingViewResponseSchema>;
export type OfferUsageStep = z.infer<typeof OfferUsageStepSchema>;
export type OfferUsageFunnel = z.infer<typeof OfferUsageFunnelSchema>;
export type OfferUsageResponse = z.infer<typeof OfferUsageResponseSchema>;
export type CreateFunnelRequest = z.infer<typeof CreateFunnelRequestSchema>;
export type UpdateFunnelRequest = z.infer<typeof UpdateFunnelRequestSchema>;
export type CreateFunnelStepRequest = z.infer<
  typeof CreateFunnelStepRequestSchema
>;
export type UpdateFunnelStepRequest = z.infer<
  typeof UpdateFunnelStepRequestSchema
>;
export type CreateOfferRequest = z.infer<typeof CreateOfferRequestSchema>;
export type UpdateOfferRequest = z.infer<typeof UpdateOfferRequestSchema>;
export type CreateRuleRequest = z.infer<typeof CreateRuleRequestSchema>;
export type UpdateRuleRequest = z.infer<typeof UpdateRuleRequestSchema>;
export type CreateOfferSetRequest = z.infer<
  typeof CreateOfferSetRequestSchema
>;
export type UpdateOfferSetRequest = z.infer<
  typeof UpdateOfferSetRequestSchema
>;
export type CreateOfferSetItemRequest = z.infer<
  typeof CreateOfferSetItemRequestSchema
>;
export type CreateTemplateRequest = z.infer<
  typeof CreateTemplateRequestSchema
>;
export type UpdateTemplateRequest = z.infer<
  typeof UpdateTemplateRequestSchema
>;
export type EvaluateRequest = z.infer<typeof EvaluateRequestSchema>;
export type EvaluateStepRequest = z.infer<typeof EvaluateStepRequestSchema>;
export type AcceptStepRequest = z.infer<typeof AcceptStepRequestSchema>;
export type DeclineStepRequest = z.infer<typeof DeclineStepRequestSchema>;
export type NavigateStepRequest = z.infer<typeof NavigateStepRequestSchema>;
export type AssignFunnelRequest = z.infer<typeof AssignFunnelRequestSchema>;
export type EvaluateFunnelResponse = z.infer<
  typeof EvaluateFunnelResponseSchema
>;
export type EvaluateStepResponse = z.infer<typeof EvaluateStepResponseSchema>;
export type StepActionResponse = z.infer<typeof StepActionResponseSchema>;
